import React, { useState } from "react";
import PropTypes from "prop-types";
import { useInView } from "react-intersection-observer";

// components
import ToggleButton from "../Global/ToggleButton";
import ResourceCard from "./ResourceCard";

// Resource page data
import { defaultResourceData } from "./resourceData";

// styles
import * as styles from "./Resources.module.css";

const Resources = ({
  title,
  subtitle,
  toggleOptions,
  resourceData,
  onCtaClick,
}) => {
  const [activeFilter, setActiveFilter] = useState("Blog");

  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.3,
  });

  // Filter resources based on active filter
  const filteredResources = resourceData.filter(resource =>
    resource.tags.includes(activeFilter)
  );

  const handleToggle = value => {
    setActiveFilter(value);
  };

  const handleCardCtaClick = (title, category) => {
    if (onCtaClick) {
      onCtaClick(title, category);
    }
  };

  return (
    <section className={styles.resourcesSection}>
      <div
        className={`container scroll_animate ${inView && "inview"}`}
        ref={ref}
      >
        {/* Section Header */}
        <div className="row">
          <div className="col-12">
            <div className={styles.sectionHeader}>
              <h1 className={styles.sectionTitle}>{title}</h1>
              {subtitle && <p className={styles.sectionSubtitle}>{subtitle}</p>}
            </div>
          </div>
        </div>

        {/* Toggle Buttons */}
        <div className="row">
          <div className="col-12">
            <ToggleButton
              options={toggleOptions}
              activeOption={activeFilter}
              onToggle={handleToggle}
            />
          </div>
        </div>

        {/* Resource Cards Grid */}
        <div className={`row ${styles.cardsGrid}`}>
          {filteredResources.length > 0 ? (
            filteredResources.map(resource => (
              <div key={resource.id} className="col-6 col-md-6 col-lg-4">
                <ResourceCard
                  image={resource.image}
                  category={resource.category}
                  title={resource.title}
                  description={resource.description}
                  ctaText={resource.ctaText}
                  ctaLink={resource.ctaLink}
                  onCtaClick={handleCardCtaClick}
                />
              </div>
            ))
          ) : (
            <div className="col-12">
              <p className={styles.noResults}>
                No resources found for this category.
              </p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

Resources.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
  toggleOptions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    })
  ),
  resourceData: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      category: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired,
      ctaText: PropTypes.string,
      ctaLink: PropTypes.string.isRequired,
      image: PropTypes.node.isRequired,
      tags: PropTypes.arrayOf(PropTypes.string).isRequired,
    })
  ),
  onCtaClick: PropTypes.func,
};

Resources.defaultProps = {
  title: "Resources",
  subtitle: "Our publications, press, and research studies",
  toggleOptions: [
    { label: "Blog", value: "Blog" },
    { label: "Media Coverage", value: "Media Coverage" },
    { label: "Press Releases", value: "Press Releases" },
    { label: "Whitepapers", value: "Whitepapers" },
    { label: "Podcasts", value: "Podcasts" },
    { label: "Publications", value: "Publications" },
  ],
  resourceData: defaultResourceData,
  onCtaClick: null,
};

export default Resources;
